/**
 * Pure transforms for matrix slot + claim CRUD.
 *
 * Counterpart to devicesEdit.ts — same shape (full library in, full
 * library out, no I/O). Each cell click in MatrixViewer's edit mode
 * lands on one of these.
 *
 * Model notes:
 *   - A SLOT is identified by (kind, channel, ccNumber|noteLabel).
 *     Adding a claim to a cell that has no slot creates the slot; adding
 *     a second claim attaches it to the same slot (co-owned).
 *   - Removing the LAST claim from a slot drops the slot entirely. An
 *     orphan slot has no reason to exist.
 *   - controlLabel is the per-claim text shown in the cell ("Fader 1");
 *     it's free-form and not constrained for uniqueness.
 */

import type {
  MatrixClaim,
  MatrixLibrary,
  MatrixSlot,
  MatrixSlotKind,
} from '@opbuttons/schema';
import { findSlot, slotKey } from '@opbuttons/schema';

// ─── Helpers ───────────────────────────────────────────────────────

/** Where a slot lives in the library. CC: (channel, ccNumber). Note:
 *  (channel, noteLabel) — labels are case-normalised for matching. */
export type SlotLocator =
  | { kind: 'cc'; channel: number; ccNumber: number }
  | { kind: 'note'; channel: number; noteLabel: string }
  | { kind: 'pc'; channel: number; pcNumber: number };

function findSlotAt(library: MatrixLibrary, loc: SlotLocator): MatrixSlot | undefined {
  return findSlot(library, loc);
}

function newSlotFor(id: string, loc: SlotLocator): MatrixSlot {
  if (loc.kind === 'cc') {
    return {
      id,
      kind: 'cc',
      channel: loc.channel,
      ccNumber: loc.ccNumber,
      claims: [],
    };
  }
  if (loc.kind === 'pc') {
    return {
      id,
      kind: 'pc',
      channel: loc.channel,
      pcNumber: loc.pcNumber,
      claims: [],
    };
  }
  return {
    id,
    kind: 'note',
    channel: loc.channel,
    noteLabel: loc.noteLabel,
    claims: [],
  };
}

// ─── Claim CRUD ────────────────────────────────────────────────────

export interface AddClaimInput {
  /** Slot id to use when creating a NEW slot. Ignored when the slot
   *  already exists at the locator. Caller supplies a ulid. */
  slotId: string;
  /** Claim id (ulid from caller). */
  claimId: string;
  deviceId: string;
  controlLabel: string;
  notes?: string;
}

/** Add a claim at `loc`. Creates the slot if it doesn't exist yet,
 *  otherwise appends to the existing slot's claims list. Returns the
 *  updated library plus the slot id the claim ended up in (useful when
 *  the caller wants to select the slot in the UI afterwards). */
export function addClaimAt(
  library: MatrixLibrary,
  loc: SlotLocator,
  input: AddClaimInput,
): { library: MatrixLibrary; slotId: string } {
  const claim: MatrixClaim = {
    id: input.claimId,
    deviceId: input.deviceId,
    controlLabel: input.controlLabel.trim() || 'Untitled control',
    ...(input.notes && input.notes.trim() ? { notes: input.notes } : {}),
  };
  const existing = findSlotAt(library, loc);
  if (existing) {
    const slots = library.slots.map((s) =>
      s.id === existing.id ? { ...s, claims: [...s.claims, claim] } : s,
    );
    return { library: { ...library, slots }, slotId: existing.id };
  }
  const slot = newSlotFor(input.slotId, loc);
  slot.claims = [claim];
  return {
    library: { ...library, slots: [...library.slots, slot] },
    slotId: slot.id,
  };
}

/** Patch a claim by its id. controlLabel and notes can change; deviceId
 *  is treated as immutable for now (changing it is equivalent to
 *  removing + re-adding, and the UI doesn't surface a "reassign claim
 *  to another device" gesture). */
export function updateClaim(
  library: MatrixLibrary,
  claimId: string,
  patch: Partial<Pick<MatrixClaim, 'controlLabel' | 'notes'>>,
): MatrixLibrary {
  const slots = library.slots.map((s) => {
    if (!s.claims.some((c) => c.id === claimId)) return s;
    const claims = s.claims.map((c) => {
      if (c.id !== claimId) return c;
      const next: MatrixClaim = {
        ...c,
        ...(patch.controlLabel !== undefined
          ? { controlLabel: patch.controlLabel.trim() || c.controlLabel }
          : {}),
      };
      if (patch.notes !== undefined) {
        if (patch.notes.trim() === '') {
          delete (next as Partial<MatrixClaim>).notes;
        } else {
          next.notes = patch.notes;
        }
      }
      return next;
    });
    return { ...s, claims };
  });
  return { ...library, slots };
}

/** Remove a claim by id. If the slot's last claim is removed, the slot
 *  itself is dropped (no orphan slots). No-op when the claim id is
 *  unknown. */
export function removeClaim(library: MatrixLibrary, claimId: string): MatrixLibrary {
  let touched = false;
  const slots: MatrixSlot[] = [];
  for (const s of library.slots) {
    const filtered = s.claims.filter((c) => c.id !== claimId);
    if (filtered.length === s.claims.length) {
      slots.push(s);
      continue;
    }
    touched = true;
    if (filtered.length > 0) {
      slots.push({ ...s, claims: filtered });
    }
    // length === 0 → drop the slot
  }
  return touched ? { ...library, slots } : library;
}

/** Remove a whole slot (and all its claims) by id. Used when the user
 *  clicks "Remove all" on a filled cell rather than removing claims
 *  one-by-one. */
export function removeSlot(library: MatrixLibrary, slotId: string): MatrixLibrary {
  const slots = library.slots.filter((s) => s.id !== slotId);
  if (slots.length === library.slots.length) return library;
  return { ...library, slots };
}

// ─── Note-row affordance ───────────────────────────────────────────

/** "Reserve" a note row by adding an empty placeholder slot at
 *  (channel, noteLabel). The MatrixViewer's note grid only renders rows
 *  for labels that appear in *some* slot — to add a brand-new row in
 *  edit mode we need to create the placeholder before the row can be
 *  clicked into. The placeholder has no claims; downstream `addClaimAt`
 *  on the same locator will append to it (per the create-or-append
 *  rule above).
 *
 *  Idempotent: if a slot already exists at (channel, noteLabel) the
 *  library is returned unchanged. */
export function reserveNoteRow(
  library: MatrixLibrary,
  slotId: string,
  channel: number,
  noteLabel: string,
): MatrixLibrary {
  const loc: SlotLocator = { kind: 'note', channel, noteLabel };
  if (findSlotAt(library, loc)) return library;
  const slot = newSlotFor(slotId, loc);
  return { ...library, slots: [...library.slots, slot] };
}

// ─── Validators ────────────────────────────────────────────────────

/** True when `channel` is in MIDI's 1..16 range. */
export function isValidChannel(channel: number): boolean {
  return Number.isInteger(channel) && channel >= 1 && channel <= 16;
}

/** True when `ccNumber` is in MIDI's 0..127 range. */
export function isValidCcNumber(n: number): boolean {
  return Number.isInteger(n) && n >= 0 && n <= 127;
}

/** Very loose note-label check — accepts letter[modifier]octave forms
 *  like "C3", "F#4", "Bb-1". Doesn't enforce the modifier set strictly
 *  because TouchOSC / other tools sometimes use unusual encodings; the
 *  user can type whatever the source device prints. Empty strings and
 *  pure whitespace are rejected. */
export function isValidNoteLabel(label: string): boolean {
  return label.trim().length > 0;
}

/** Convenience for callers — same key the schema's slotKey() produces,
 *  re-exported here so the slot-edit module is self-contained for
 *  tooling that prefers a single import. */
export { slotKey };

/** Used by SlotEditor to decide between empty-cell and filled-cell UI. */
export function findSlotByLocator(library: MatrixLibrary, loc: SlotLocator): MatrixSlot | undefined {
  return findSlotAt(library, loc);
}

/** Discriminator helper for SlotLocator — used by callers that build a
 *  locator from a runtime kind string. */
export function makeLocator(
  kind: MatrixSlotKind,
  channel: number,
  ccOrLabel: number | string,
): SlotLocator {
  if (kind === 'cc') return { kind: 'cc', channel, ccNumber: ccOrLabel as number };
  if (kind === 'pc') return { kind: 'pc', channel, pcNumber: ccOrLabel as number };
  return { kind: 'note', channel, noteLabel: ccOrLabel as string };
}

/** Reserve an empty PROGRAM row so the viewer can show it before anything
 *  claims it - the pc twin of `reserveNoteRow`, and needed for the same
 *  reason: unlike CC, the viewer does not render a full 0..127 program grid
 *  (a rig uses a handful of programs, not 128), so a row has to be created
 *  before a claim can be dropped into it. Idempotent. */
export function reservePcRow(
  library: MatrixLibrary,
  slotId: string,
  channel: number,
  pcNumber: number,
): MatrixLibrary {
  const loc: SlotLocator = { kind: 'pc', channel, pcNumber };
  if (findSlotAt(library, loc)) return library;
  return { ...library, slots: [...library.slots, newSlotFor(slotId, loc)] };
}
